import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { TOKEN_EXPIRED } from '@speeki/dictionary';
import { StorageManager } from '@speeki/storage-manager';

import { TranslationsDocument } from '@graphql/generated/graphql';

import i18n from '@services/TranslationsService/i18n';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
    authVar,
    currentLanguageVar,
    initLoadingVar,
    setApolloActionStatus,
    translationsStatusVar,
} from '@boot/reactiveVariables';

/**
 * Single-use hook (only in RootRoutes.tsx).
 */
export const useTranslations = () => {
    const auth = useReactiveVar(authVar);
    const [firstInit, setFirstInit] = useState(true);
    const currentLanguage = useReactiveVar(currentLanguageVar);
    const initLoading = useReactiveVar(initLoadingVar);

    const [getTranslations, { data, error, loading }] = useLazyQuery(
        TranslationsDocument,
        {
            context: {
                globalUrl: !auth.isMfa && !auth.isLoggedIn,
                notifyOnError: {
                    exceptions: [TOKEN_EXPIRED],
                },
            },
            notifyOnNetworkStatusChange: true,
        },
    );

    useEffect(() => {
        if (initLoading) return;

        StorageManager.setValue('language', currentLanguage);
        void getTranslations({
            variables: {
                targetLanguage: currentLanguage,
            },
        });
    }, [currentLanguage]);

    useEffect(() => {
        const isResourceIncluded = Object.keys(i18n.store.data).includes(
            currentLanguage,
        );

        if (data) {
            if (!isResourceIncluded || firstInit) {
                /*
                 * Merged in, not assigned over. `addResource` with an empty key
                 * replaced the whole namespace, so a string the backend had no
                 * row for stopped resolving and rendered as its own key - the
                 * bundled English was never a fallback, only what showed until
                 * the first response landed. Merging leaves it as the floor.
                 */
                i18n.addResourceBundle(
                    currentLanguage,
                    'translations',
                    data.translations,
                    true,
                    true,
                );
                i18n.addResourceBundle(
                    currentLanguage,
                    'userRelationship',
                    data.userRelationship,
                    true,
                    true,
                );
                i18n.addResourceBundle(
                    currentLanguage,
                    'countries',
                    data.countries,
                    true,
                    true,
                );
                i18n.addResourceBundle(
                    currentLanguage,
                    'caseStatus',
                    data.caseStatus,
                    true,
                    true,
                );
            }
            void i18n.changeLanguage(currentLanguage);
            setFirstInit(false);

            import(
                /* @vite-ignore */ `dayjs/locale/${currentLanguage.toLowerCase()}.js`
            )
                .then(() => dayjs.locale(currentLanguage.toLowerCase()))
                .catch(() => dayjs.locale('en'));
        }
    }, [data]);

    useEffect(() => {
        translationsStatusVar(
            setApolloActionStatus({ error: !!error, loading, success: !!data }),
        );
    }, [error, data, loading]);

    return {
        currentLanguage,
        getTranslations,
    };
};
